<template>
    <div class="mt-3">
        <div class="py-3 mb-3" v-if="type == 'targets'">
            <b-container fluid>
                <b-row>
                    <b-col>
                        <average-item
                            :text="$t('targets.totalWeight')"
                            :performance="toNumber(consolidated.totalWeight)"
                            :progress="getProgress(consolidated.totalWeight)"
                            :max="toNumber(consolidated.totalWeight)"
                        />
                    </b-col>
                    <b-col>
                        <average-item
                            :text="$t('targets.averageAchieved')"
                            :performance="toNumber(consolidated.achieved)"
                            :progress="getProgress(consolidated.achieved)"
                            :max="toNumber(consolidated.totalWeight)"
                        />
                    </b-col>
                    <b-col v-if="showSelfEvaluation">
                        <average-item
                            :text="$t('targets.averageSelfEvaluation')"
                            :performance="toNumber(consolidated.ownerEvaluations)"
                            :progress="getProgress(consolidated.ownerEvaluations)"
                            :max="toNumber(consolidated.totalWeight)"
                        />
                    </b-col>
                    <b-col v-if="showManagerEvaluation">
                        <average-item
                            :text="$t('targets.averageManagerEvaluation')"
                            :performance="toNumber(consolidated.managerEvaluations)"
                            :progress="getProgress(consolidated.managerEvaluations)"
                            :max="toNumber(consolidated.totalWeight)"
                        />
                    </b-col>
                    <b-col v-if="showCommitteeEvaluation">
                        <average-item
                            :text="$t('targets.averageCommitteeEvaluation')"
                            :performance="toNumber(consolidated.committeeEvaluations)"
                            :progress="getProgress(consolidated.committeeEvaluations)"
                            :max="toNumber(consolidated.totalWeight)"
                        />
                    </b-col>
                </b-row>
            </b-container>
        </div>
        <div v-for="item in targets" :key="'list-target-' + item.id">
            <target
                :goal="item"
                :isUserAllowedToEdit="isUserAllowedToEdit"
                @reload="updated()"
                :type="type"
                v-if="type !== 'library-goals'"
            />
            <hire-goal
                v-if="type === 'library-goals'"
                :goal="item"
                :canHireGoal="canHireGoal"
                @changed="hiringGoal($event)"
                :ref="'target-switch-' + item.id"
            />

            <reason-modal v-if="type === 'library-goals'" :target="item" @hide="onHideHandler($event)" />
        </div>

        <div class="d-flex justify-content-end" v-if="targets.length && type === 'library-goals' && canHireGoal">
            <c-button-loader variant="success" @click="onClickHire" :loader="isSubmiting" :disabled="isSubmiting">
                {{ $t('form.button.hire') }}
            </c-button-loader>
        </div>
    </div>
</template>

<script>
import Target from '../Target/Target'
import HireGoal from '../HireGoal/HireGoal'
import ReasonModal from '../ReasonModal/ReasonModal'
import CButtonLoader from '@/views/components/button-loader'
import { hireGoals } from './api'
import { success, danger } from '@/lib/notification'
import i18n from '@/i18n'
import { AverageItem } from './components'
import { userPermissionsMixin } from '@/mixins'

export default {
    name: 'List',
    mixins: [userPermissionsMixin],
    components: {
        Target,
        HireGoal,
        CButtonLoader,
        ReasonModal,
        AverageItem,
    },
    props: {
        targets: {
            type: Array,
            required: true,
        },
        isUserAllowedToEdit: {
            type: Boolean,
            required: true,
        },
        currentUserHasRolePermissions: {
            type: Boolean,
            required: true,
        },
        type: {
            type: String,
            required: false,
        },
        consolidated: {
            type: Object,
            required: false,
        },
        isProfileOwner: {
            type: Boolean,
            required: false,
        },
        userId: {
            type: [Number, String],
            required: false,
        },
        update: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isSubmiting: false,
        }
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
        showSelfEvaluation() {
            return this.hasEventPermission(
                this.currentUser.permissions,
                'goals',
                'consolidated',
                'allow_view_self_evaluation'
            )
        },
        showManagerEvaluation() {
            return this.hasEventPermission(
                this.currentUser.permissions,
                'goals',
                'consolidated',
                'allow_view_manager_evaluation'
            )
        },
        showCommitteeEvaluation() {
            return this.hasEventPermission(
                this.currentUser.permissions,
                'goals',
                'consolidated',
                'allow_view_committee_evaluation'
            )
        },
        canHireGoal() {
            return this.isProfileOwner || this.currentUserHasRolePermissions
        },
    },
    created() {
        if (this.type === 'library-goals') {
            this.targets.forEach((target) => {
                target.hired = true
            })
        }
    },
    methods: {
        getProgress(progress) {
            return progress == undefined ? 0 : parseFloat(progress)
        },
        toNumber(string) {
            return parseFloat(string)
        },
        updated() {
            this.update()
        },
        openModal(id) {
            this.$bvModal.show('reasonModal-' + id)
        },
        hiringGoal({ id, ev }) {
            if (!ev) {
                this.openModal(id)
            } else {
                this.addToHiredGoals(id, '', true)
            }
        },
        onClickHire() {
            this.isSubmiting = true
            const form = {
                user_id: this.userId,
                indicators: this.targets.map((target) => {
                    const { id, reason, hired } = target
                    return {
                        reason,
                        hired,
                        parent_id: id,
                    }
                }),
            }
            hireGoals(form)
                .then(() => {
                    this.update()
                    this.notificationTargetHiredSuccessfully()
                })
                .catch((e) => {
                    console.error(e)
                    this.notificationError()
                })
                .finally(() => {
                    this.isSubmiting = false
                })
        },
        onHideHandler(event) {
            if (!event.reason.length) {
                const index = `target-switch-${event.id}`
                this.$refs[index][0].check(true)
                this.addToHiredGoals(event.id, '', true)
            } else {
                this.addToHiredGoals(event.id, event.reason, false)
            }
        },
        addToHiredGoals(targetId, reason, hired) {
            const exists = this.targets.find((target) => target.id == targetId)
            const index = this.targets.indexOf(exists)
            if (exists) {
                this.targets[index].reason = reason
                this.targets[index].hired = hired
            }
        },
        notificationTargetHiredSuccessfully() {
            success(
                i18n.t('notification.hiredSuccessfully', {
                    item: i18n.t('targets.targets'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
}
</script>
